import { MTK_DECIMALS } from '@meteraprotocol/core';
import { initNufiDappCardanoSdk } from '@nufi/dapp-client-cardano';
import nufiCoreSdk from '@nufi/dapp-client-core';
import axios, { AxiosResponse } from 'axios';
import { BigRational } from 'big-rational-ts';
import BigNumber from 'bignumber.js';
import JSONBig from 'json-bigint';
import { C, fromHex, getAddressDetails, UTxO } from 'lucid-txpipe';
import { ApiErrors } from '../../errors.js';
import {
  ArrayElement,
  IPortfolioState,
  Order,
  WalletData,
} from '../../types.js';
import { bigNumberToBigRational } from '../../utils.js';

export const getNufiWalletInitAddres = () => {
  const network = process.env.NEXT_PUBLIC_NETWORK as
    | 'Mainnet'
    | 'Preview'
    | 'Preprod'
    | 'Custom';

  switch (network) {
    case 'Mainnet':
      return 'https://wallet.nu.fi';
    case 'Preprod':
      return 'https://wallet-testnet-staging.nu.fi';
    case 'Preview':
      return 'https://wallet-preview-staging.nu.fi';
    default:
      return '';
  }
};

export const ValidWallets = [
  'nami',
  'eternl',
  'vespr',
  'flint',
  'typhon',
  'nufi',
  'lace',
  'metamask',
] as const;

export type ValidWallet = (typeof ValidWallets)[number];

export type Balance = Record<string, bigint>;

/**
 * Lib used parse/ stringify big integers from json
 * without losing precision (up to bigint precision)
 *
 */
export const JSONbig = JSONBig({
  alwaysParseAsBig: true,
  useNativeBigInt: true,
  strict: true,
});

/**
 * Turns utxo into the right collateral balance
 *
 * @param utxo - The utxo to be converted
 * @returns The collateral of the utxo as `txHash#outputIndex`
 */
export const formatCollateralUTXO = (utxo: UTxO) =>
  `${utxo.txHash}#${utxo.outputIndex}`;

export async function getWalletBalance(
  wallet: WalletData['wallet'],
): Promise<Balance> {
  const changeAddress = getAddressDetails(await wallet.getChangeAddress())
    .address.bech32;
  const utxosHex = await wallet.getUtxos();
  if (!utxosHex) {
    return {};
  }

  const ownedUtxos = utxosHex
    .map(fromHex)
    .map(C.TransactionUnspentOutput.from_bytes)
    .filter((utxo) => {
      const utxoAddr = JSON.parse(utxo.output().address().to_json());
      return utxoAddr === changeAddress;
    });

  /**
   * [`${policy}${hexName}`] : amount
   */
  const partialBalance: Record<string, bigint> = { lovelace: 0n };

  for (const utxo of ownedUtxos) {
    const output = utxo.output();
    const lovelace = output.amount().coin();
    partialBalance['lovelace'] += BigInt(lovelace.to_str());
    const multiAsset = output.amount().multiasset();
    const value: Record<
      string,
      Record<string, string>
    > = multiAsset?.to_js_value() || {};
    for (const [policy, assetNamesDict] of Object.entries(value)) {
      for (const [hexName, amount] of Object.entries(assetNamesDict)) {
        const unit = `${policy}${hexName}`;
        if (!partialBalance[unit]) {
          partialBalance[unit] = BigInt(0);
        }
        partialBalance[unit] += BigInt(amount);
      }
    }
  }
  return partialBalance;
  // return {};
}

/**
 * Gets the wallet that's going to be used to deposit/withdraw.
 *
 * @param window - The window object of the browser.
 * @returns the wallet API.
 */
export async function getWallet(
  window: Window,
  walletName: ValidWallet = 'eternl',
) {
  if (walletName === 'metamask') {
    // Should be called before accessing `window.cardano.nufiSnap`
    initNufiDappCardanoSdk(nufiCoreSdk, 'snap');
    const walletAPI = await window.cardano?.nufiSnap?.enable();
    return walletAPI;
  }
  if (window?.cardano?.[walletName]) {
    const walletAPI = await window.cardano?.[walletName]?.enable();
    return walletAPI;
  }
  console.log('Couldnt find wallet');
  throw new Error("Couldn't find the wallet API");
}

/**
 * Given the state of a portfolio, it calculates how much an MTK is worth
 * @returns The price of a single MTK
 */
export function getMTKPrice(portfolio: IPortfolioState) {
  const tvl = portfolio.assets.reduce(
    (acc, { price, amount, asset: { decimals } }) => {
      return bigNumberToBigRational(BigNumber(price))
        .mul(new BigRational(amount, 1n))
        .mul(new BigRational(1n, BigInt(10 ** decimals)))
        .add(acc);
    },
    new BigRational(0n, 1n),
  );
  return tvl.div(new BigRational(portfolio.supply, 1n)).reduce();
}

/**
 * Decimal to bigint safe conversion
 *
 * @param decimal - The decimal to be converted
 * @param method - Whether to ceil or floor the decimal
 * @returns The converted decimal as a bigint
 */
export function decimalToBigInt(
  decimal: BigNumber,
  roundingMode: 'floor' | 'ceil',
) {
  const roundingModeMap = {
    floor: BigNumber.ROUND_FLOOR,
    ceil: BigNumber.ROUND_CEIL,
  };

  // Use toFixed(0) to remove scientific notation when going to string
  return BigInt(decimal.toFixed(0, roundingModeMap[roundingMode]));
}

/*
 * Checks that the wallet balance is enough for the interaction
 * @param walletBalance - The balance of the wallet
 * @param interactionBalance - The balance needed for the interaction
 */
export function isBalanceEnough(
  walletBalance: Balance,
  interactionBalance: Balance,
) {
  return Object.entries(interactionBalance).every(
    ([unit, amount]) => (walletBalance[unit] || BigInt(0)) >= amount,
  );
}

/** Function to serialize bigints properly,
 * if called with json bigint in the param.
 * It doesn't trigger the serialization mechanism of axios.
 * It also parses the body properly
 */
export async function postToAPI(url: string, data: string) {
  return await axios.post(url, data, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    transformRequest: [(data) => data],
    transformResponse: [(data) => JSONbig.parse(data)],
  });
}

/**
 * Function to get the unit of an asset in hex format
 */
export function assetToUnit(
  asset: ArrayElement<IPortfolioState['assets']>['asset'],
) {
  return asset.currencySymbol === ''
    ? 'lovelace'
    : `${asset.currencySymbol}${asset.name}`;
}

/**
 * Given an error that's coming from the API, it returns a user friendly error
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleApiError = (err: any): ApiError => {
  if (
    err instanceof axios.AxiosError &&
    err.response?.data &&
    Object.values(ApiErrors).includes(err.response?.data)
  ) {
    const data = err.response.data;
    switch (data) {
      case ApiErrors.INVALID_ORDER:
        // TODO handle refresh
        return {
          error:
            'The data in your order is probably outdated. Refresh the page and try again',
        };
      case ApiErrors.ORDER_EXPIRED:
        return { error: 'This order expired, try again.' };
      case ApiErrors.ALREADY_CANCELLED:
        return { error: 'This order was cancelled already' };
      case ApiErrors.NOT_ENOUGH_MONEY:
        return {
          error:
            'You ran out of tokens. Refresh your wallet and try again if you recharged',
        };
      case ApiErrors.BAD_REQUEST:
        return {
          error: 'Some of your input is wrong',
        };
      case ApiErrors.ALREADY_CANCELLING:
        return { error: 'In process of cancelling' };
      default:
        return { error: data };
    }
  } else {
    if (err.message.includes('ECONNREFUSED')) {
      return {
        error: 'Our servers are currently down. Please try again later',
      };
    }
  }
  return { error: 'Unknown error. Try again' };
};

/**
 * Function that checks if the user has enough tokens for the interaction
 */
export function userHasEnoughBalance(
  userBalance: Balance,
  interactionBalanceWithIds: Balance,
  type: 'mint' | 'burn',
  inputValue: BigNumber,
  portfolioState: IPortfolioState,
): boolean {
  const interactionBalanceWithUnits = Object.fromEntries(
    Object.entries(interactionBalanceWithIds).map(([id, amount]) => {
      const asset = portfolioState.assets.find(
        (asset) => asset.asset.id === id,
      )!;
      return [assetToUnit(asset.asset), amount];
    }),
  );

  return type === 'mint'
    ? isBalanceEnough(userBalance, interactionBalanceWithUnits)
    : BigNumber(
        (userBalance[portfolioState.portfolio.mtkUnit] || 0n).toString(),
      )
        .div(10 ** MTK_DECIMALS)
        .gte(inputValue);
}

export function getOrderStatus(order: Order) {
  const status =
    order.validationFailures?.length && !order.cancelledAt
      ? 'Invalid'
      : order.expiredAt
        ? 'Failed'
        : order.orderProcessedAt
          ? order.slippageFail
            ? 'Failed'
            : 'Confirmed'
          : order.processSubmittedAt
            ? 'Processing'
            : order.cancelledAt
              ? 'Cancelled'
              : order.cancelSubmittedAt
                ? 'Cancelling'
                : order.orderPlacedAt
                  ? 'Pending'
                  : 'Processing';
  return status;
}

/**
 * Handler function types
 */
export type APIEventHandlers = {
  onError: (err: string) => void;
  onSuccess: (err: string) => void;
};

export type ApiError = { error: string };

export type ApiResponse = AxiosResponse;
export const isError = (err: Response): err is ApiError =>
  Object(err).hasOwnProperty('error');
type Response = ApiError | ApiResponse;
